import { sortBy } from "lodash";
import colors from "tailwindcss/colors";

import themeColors from "@config/themeColors.json";
import { formatVolume } from "@utils/formatVolume";

// Chart colours are SVG attributes, so they are colour strings rather than
// Tailwind classes. They come from the theme and Tailwind's own palette, never
// literals, so the charts follow either when it changes.
//
// Categorical, in fixed order: primary-1, amber-600, violet-600, green-600,
// pink-700, checked for colour-blind separation. Past five series, the first
// four keep their colours and the rest share OTHER_COLOR as one "Other" series.
const COLORS = [
  themeColors.primary[1],
  colors.amber[600],
  colors.violet[600],
  colors.green[600],
  colors.pink[700],
];
const OTHER_COLOR = colors.gray[400];

/**
 * The series to draw for these sorted keys, folding any past the palette, and
 * any key that is `other` itself, into one `other` series.
 */
export const foldSeries = (keys: string[], other: string) => {
  const named = keys.filter(key => key !== other);
  const kept =
    keys.length > COLORS.length ? named.slice(0, COLORS.length - 1) : named;

  return {
    series: kept.length < keys.length ? [...kept, other] : kept,
    seriesOf: (key: string) => (kept.includes(key) ? key : other),
    colorOf: (series: string, index: number) =>
      series === other ? OTHER_COLOR : COLORS[index],
  };
};

/**
 * The declarations without their reversal pairs, which the ledger nets, so no
 * chart counts either half. Each meter usage adjustment pairs with the latest
 * click-over reading before it at the same offtake whose volume it negates,
 * if one is still unpaired; every other declaration stays, however its volume
 * opposes another's. See "Reversal pair" in GLOSSARY.md.
 */
export const netDeclarations = <
  D extends {
    extractionPointId?: string;
    volume?: number | string;
    tag?: string | null;
    readAt?: string | null;
  },
>(
  declarations: D[],
): D[] => {
  // Unpaired click-over readings by offtake and volume, oldest first.
  const clickOvers = new Map<string, D[]>();
  const cancelled = new Set<D>();
  for (const declaration of sortBy(declarations, d =>
    Date.parse(d.readAt ?? ""),
  )) {
    const volume = Number(declaration.volume);
    if (!volume) continue;
    const key = (litres: number) =>
      `${declaration.extractionPointId}|${litres}`;
    if (declaration.tag === "click_over") {
      clickOvers.set(key(volume), [
        ...(clickOvers.get(key(volume)) ?? []),
        declaration,
      ]);
    } else if (declaration.tag === "meter_usage_adjustment") {
      const reading = clickOvers.get(key(-volume))?.pop();
      if (reading) cancelled.add(reading).add(declaration);
    }
  }
  return declarations.filter(declaration => !cancelled.has(declaration));
};

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

/** The period's twelve calendar months (1-12) in order, each with its
 * `shared.months.short` key. */
export const periodMonths = (periodStart?: string) => {
  // periodStart is ISO in the scheme's offset; its month digits are the
  // scheme's month wherever the viewer is, unlike Date#getMonth.
  const startMonth = Number(periodStart?.slice(5, 7)) || 1;

  return MONTHS.map((_, i) => {
    const month = ((startMonth - 1 + i) % 12) + 1;
    return { month, labelKey: `shared.months.short.${MONTHS[month - 1]}` };
  });
};

/**
 * A monthly chart of declarations over the accounting period that starts at
 * `periodStart`: its series, folded by foldSeries with `other` naming the
 * fold, and its dataset, one row per month in period order whose `month` is
 * `label` of the month's `shared.months.short` key and whose other fields
 * hold each series' litres, 0 for none.
 *
 * `entriesOf` lists one declaration's [series key, litres] pairs from its
 * class proportions. Declarations in month 0, opening readings, are left out.
 */
export const monthlySeries = (
  declarations: { classProportions?: any }[],
  periodStart: string | undefined,
  entriesOf: (classProportions: any) => [string, number | string][],
  other: string,
  label: (monthKey: string) => string,
) => {
  // usage[calendar month][series key], in litres.
  const usage: Record<number, Record<string, number>> = {};
  for (const { classProportions } of declarations) {
    const month = Number(classProportions?.month);
    if (!month) continue;
    for (const [key, volume] of entriesOf(classProportions)) {
      usage[month] ??= {};
      usage[month][key] = (usage[month][key] ?? 0) + Number(volume);
    }
  }

  const keys = Array.from(
    new Set(Object.values(usage).flatMap(Object.keys)),
  ).sort();
  const { series, seriesOf, colorOf } = foldSeries(keys, other);
  const dataset = periodMonths(periodStart).map(({ month, labelKey }) => {
    const row: Record<string, number | string> = {
      month: label(labelKey),
      ...Object.fromEntries(series.map(name => [name, 0])),
    };
    for (const [key, volume] of Object.entries(usage[month] ?? {})) {
      const name = seriesOf(key);
      row[name] = (row[name] as number) + volume;
    }
    return row;
  });

  return { series, colorOf, dataset };
};

// The styling both monthly charts share: a light grid, small grey month and
// volume ticks, and a legend of coloured dots with the names in ink.
export const GRID_PROPS = {
  vertical: false,
  stroke: colors.gray[200],
} as const;
export const X_AXIS_PROPS = {
  dataKey: "month",
  interval: "preserveStartEnd",
  minTickGap: 4,
  tickLine: false,
  axisLine: { stroke: colors.gray[200] },
  tick: { fill: colors.gray[500], fontSize: 11 },
} as const;
export const Y_AXIS_PROPS = {
  tickLine: false,
  axisLine: false,
  tick: { fill: colors.gray[500], fontSize: 11 },
  tickFormatter: (value: number) => formatVolume(value, "", 0),
} as const;
export const LEGEND_PROPS = {
  iconType: "circle",
  iconSize: 8,
  wrapperStyle: { fontSize: 12 },
  // Series colour marks identity; the name stays in ink.
  formatter: (value: string) => <span className="text-gray-700">{value}</span>,
} as const;
